import React, { useEffect } from 'react';

import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { loadTwilioRegistrationSummary } from '../../actions/load-twilio-registration-summary';
import {
  twilioRegistrationSummarySelector,
  twilioRegistrationSummaryStatusSelector,
} from '../../admin-selectors';

import { accountCarrierRegistration, orgAdminRoute } from '../../../web-routes';

import { TwilioRegistrationInfoTable } from './twilio-registration-info-table';

import { ACCOUNT_STATUS_CONFIG } from '../../../util/account-status';
import { formatDate, formatRelativeDate } from '../../../util/formatters';
import { LoadingMessage } from '../../../widgets/loading-message';

export const FailedTwilioRegistrations = () => {
  const dispatch = useDispatch();
  const twilioRegistrationSummary = useSelector(twilioRegistrationSummarySelector, shallowEqual);
  const twilioRegistrationSummaryStatus = useSelector(
    twilioRegistrationSummaryStatusSelector,
    shallowEqual,
  );

  useEffect(() => {
    dispatch(loadTwilioRegistrationSummary());
  });

  if (twilioRegistrationSummaryStatus.loading) {
    return <LoadingMessage />;
  }

  if (twilioRegistrationSummaryStatus.error) {
    return <div>The request to load the status was unsuccessful.</div>;
  }

  const {
    missingInformationAccounts,
    failedBundleRegistrations,
    failedCampaignRegistrations,
    inProgressBundleRegistrations,
    inProgressCampaignRegistrations,
    campaignRegistrationSummary,
  } = twilioRegistrationSummary;

  missingInformationAccounts.sort((a, b) => a.accountId - b.accountId);
  failedBundleRegistrations.sort((a, b) => a.accountId - b.accountId);
  failedCampaignRegistrations.sort((a, b) => a.org_id - b.org_id);
  inProgressBundleRegistrations.sort((a, b) => a.accountId - b.accountId);
  inProgressCampaignRegistrations.sort((a, b) => a.org_id - b.org_id);
  campaignRegistrationSummary.sort((a, b) => a.weekOfYear - b.weekOfYear);

  let averageAgeOfInProgressCampaigns = 0;
  if (inProgressCampaignRegistrations.length) {
    inProgressCampaignRegistrations.forEach((c) => {
      averageAgeOfInProgressCampaigns += moment(c.twilioCampaignSubmittedAt).diff(moment(), 'days');
    });
    // It is negative because they were submitted in the past relative to today
    averageAgeOfInProgressCampaigns *= -1;
    averageAgeOfInProgressCampaigns /= inProgressCampaignRegistrations.length;
    // Limit to two digits
    averageAgeOfInProgressCampaigns = +averageAgeOfInProgressCampaigns.toFixed(2);
  }

  return (
    <>
      <p className="typo-label u-margin-y">Failed Bundle Registrations</p>
      <p className="typo-subtitle">
        These are times when the top-level customer information, like account name and EIN, did not
        go through. To fix, we need to confirm the business information with the customer and click
        the retry bundle registration button in from the Twilio Business Information admin area.
      </p>
      <TwilioRegistrationInfoTable
        rows={failedBundleRegistrations}
        rowIdKey="accountId"
        columns={[
          {
            key: 'accountName',
            label: 'Account Name',
            linkGenerator: (account) => accountCarrierRegistration(account.accountId),
          },
          {
            label: 'Status',
            key: 'status',
            valueDisplayGenerator: (account) =>
              ACCOUNT_STATUS_CONFIG[account.status]
                ? ACCOUNT_STATUS_CONFIG[account.status].text
                : '',
          },
          {
            key: 'Organizations',
            label: 'Organization Links',
            linkGenerator: (account) =>
              account.Organizations.map((o) => ({
                link: orgAdminRoute(o.org_id),
                value: o.name,
              })),
          },
          {
            key: 'twilioTrustHubBundleSid',
            label: 'Bundle SID',
            absoluteLinkGenerator: (account) =>
              `https://console.twilio.com/us1/develop/sms/regulatory-compliance/brands/${account.twilioTrustHubBundleSid}`,
          },
          {
            key: 'twilioBundleType',
            label: 'Bundle Type',
          },
        ]}
      />
      <p className="typo-label u-margin-y">Customers Who Need To Give Us Information</p>
      <p className="typo-subtitle">
        These customers have not provided an EIN but need to. We just need to get their information,
        and enter it into the admin area.
      </p>
      <TwilioRegistrationInfoTable
        rows={missingInformationAccounts}
        rowIdKey="accountId"
        columns={[
          {
            key: 'accountName',
            label: 'Account Name',
            linkGenerator: (account) => accountCarrierRegistration(account.accountId),
          },
          {
            label: 'Status',
            key: 'status',
            valueDisplayGenerator: (account) =>
              ACCOUNT_STATUS_CONFIG[account.status]
                ? ACCOUNT_STATUS_CONFIG[account.status].text
                : '',
          },
          {
            key: 'Organizations',
            label: 'Organization Links',
            linkGenerator: (account) =>
              account.Organizations.map((o) => ({
                link: orgAdminRoute(o.org_id),
                value: o.name,
              })),
          },
        ]}
      />
      <p className="typo-label u-margin-y">Failed Campaign Registrations</p>
      <p className="typo-subtitle">
        These are when the campaign registration did not go through. These require manual
        troubleshooting with Twilio, as we do not expect these to fail.
      </p>
      <TwilioRegistrationInfoTable
        rows={failedCampaignRegistrations}
        rowIdKey="org_id"
        columns={[
          {
            key: 'name',
            label: 'Organization Name',
            linkGenerator: (org) => orgAdminRoute(org.org_id),
          },
          {
            key: 'messageServiceSid',
            label: 'Messaging Service SID',
            absoluteLinkGenerator: (org) =>
              `https://console.twilio.com/us1/service/sms/${org.messageServiceSid}/sms-senders`,
          },
        ]}
      />
      <p className="typo-label u-margin-y">Pending Bundle Registrations</p>
      <p className="typo-subtitle">
        These are the registrations that we are waiting to hear back from Twilio on
      </p>
      <TwilioRegistrationInfoTable
        rows={inProgressBundleRegistrations}
        rowIdKey="accountId"
        columns={[
          {
            key: 'accountName',
            label: 'Account Name',
            linkGenerator: (account) => accountCarrierRegistration(account.accountId),
          },
        ]}
      />
      <p className="typo-label u-margin-y">Pending Campaign Registrations</p>
      <p className="typo-subtitle">
        These are the campaigns we are waiting to hear back from Twilio on
      </p>
      <p>Average age of campaigns: {averageAgeOfInProgressCampaigns}</p>
      <TwilioRegistrationInfoTable
        rows={inProgressCampaignRegistrations}
        rowIdKey="org_id"
        columns={[
          {
            key: 'name',
            label: 'Organization Name',
            linkGenerator: (org) => orgAdminRoute(org.org_id),
          },
          {
            key: 'messageServiceSid',
            label: 'Messaging Service SID',
            absoluteLinkGenerator: (org) =>
              `https://console.twilio.com/us1/service/sms/${org.messageServiceSid}/sms-senders`,
          },
          {
            key: 'twilioCampaignSubmittedAt',
            label: 'Submitted At',
            valueDisplayGenerator: (org) =>
              `${formatDate(org.twilioCampaignSubmittedAt)} ${
                org.twilioCampaignSubmittedAt
                  ? `(${formatRelativeDate(org.twilioCampaignSubmittedAt)})`
                  : ''
              }`,
          },
        ]}
      />
      <p className="typo-label u-margin-y">Registration Summary Stats</p>
      <p className="typo-subtitle">
        Split out by weeks after 5/01/2023 (the date where we reliably started tracking metrics like
        this), this is the average registration time and the number of registrations that have
        happen on a given week
      </p>
      <TwilioRegistrationInfoTable
        rows={campaignRegistrationSummary}
        rowIdKey="weekOfYear"
        columns={[
          {
            key: 'weekOfYear',
            label: 'Week of Year',
          },
          {
            key: 'averageRegistrationTime',
            label: 'Average Registration Time (Days)',
            valueDisplayGenerator: (registrationRow) =>
              registrationRow.averageRegistrationTime?.toFixed(2) || '',
          },
          {
            key: 'numberOfRegistrations',
            label: 'Number of Registrations',
          },
        ]}
      />
    </>
  );
};
